/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */
import { addListener } from '../api';
import { buildAuthenticatedWsUrl } from './buildAuthenticatedWsUrl';
import { buildPingMessage, buildSubscribeMessage, buildUnsubscribeMessage } from './buildOutboundMessage';
import { getWsUrl } from './getWsHostname';
import {
  getHandlersForMessage,
  handleProtocolMessage,
  parseInboundMessage,
  shouldIgnoreInboundMessage,
} from './handleInboundMessage';
import {
  CALLBACK_ON_CONNECT,
  CALLBACK_ON_DISCONNECT,
  CALLBACK_ON_ERROR,
  CALLBACK_ON_MESSAGE,
  CALLBACK_ON_SUBSCRIBED,
  CALLBACK_ON_UNSUBSCRIBED,
  ERROR_TYPE_CONNECTION,
  ERROR_TYPE_MESSAGE,
  ERROR_TYPE_SUBSCRIBE,
  ERROR_TYPE_UNSUBSCRIBE,
  WIRE_ACTION_SUBSCRIBE,
  WIRE_ACTION_UNSUBSCRIBE,
} from './messageTypes';
import { resolveConnexCredentials } from './resolveCredentials';

const DEFAULT_PING_INTERVAL_MS = 30000;
const DEFAULT_RECONNECT_BASE_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30000;

const createUUID = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const getTopicKey = (service, eventType) => JSON.stringify([service, eventType]);

export const createUiPushClient = () => {
  const subscriptions = new Map();
  const topicRefCount = new Map();
  const serverSubscribed = new Set();
  const pendingWireOps = new Map();

  let config = {
    getCredentials: resolveConnexCredentials,
    pingIntervalMs: DEFAULT_PING_INTERVAL_MS,
    reconnectBaseDelayMs: DEFAULT_RECONNECT_BASE_DELAY_MS,
    wsPath: '',
    getWsUrl: hostname => getWsUrl(hostname, ''),
  };

  let socket = null;
  let pingTimer = null;
  let reconnectTimer = null;
  let reconnectAttempt = 0;
  let credentialsListenerRegistered = false;
  let lastCredentialsKey = null;
  let outboundQueue = [];

  const getTotalRefCount = () => {
    let total = 0;

    topicRefCount.forEach(topic => {
      total += topic.count;
    });

    return total;
  };

  const notifyAllSubscriptions = (callbackName, payload) => {
    subscriptions.forEach(subscription => {
      subscription?.[callbackName]?.(payload);
    });
  };

  const notifyTopicSubscriptions = (topicKey, callbackName, payload) => {
    subscriptions.forEach(subscription => {
      if (subscription?.topicKey === topicKey) {
        subscription?.[callbackName]?.(payload);
      }
    });
  };

  const getCredentialsKey = credentials =>
    `${credentials?.entityRef ?? ''}:${credentials?.token ?? ''}:${credentials?.apiKey ?? ''}`;

  const getCredentials = () => config.getCredentials();

  const hasValidCredentials = credentials =>
    Boolean(credentials?.entityRef && credentials?.token && credentials?.apiKey);

  const clearPingTimer = () => {
    if (pingTimer) {
      clearInterval(pingTimer);
      pingTimer = null;
    }
  };

  const clearReconnectTimer = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const clearWireState = () => {
    serverSubscribed.clear();
    pendingWireOps.clear();
    outboundQueue = [];
  };

  const sendOnSocket = payload => {
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      outboundQueue.push(payload);

      return;
    }

    socket.send(JSON.stringify(payload));
  };

  const flushOutboundQueue = () => {
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      return;
    }

    while (outboundQueue.length > 0) {
      const payload = outboundQueue.shift();
      socket.send(JSON.stringify(payload));
    }
  };

  const sendPing = () => {
    const credentials = getCredentials();

    sendOnSocket(
      buildPingMessage({
        entityRef: credentials?.entityRef,
        token: credentials?.token,
        apiKey: credentials?.apiKey,
      })
    );
  };

  const startPinging = () => {
    clearPingTimer();
    pingTimer = setInterval(sendPing, config.pingIntervalMs);
  };

  const sendWireSubscribe = ({ service, eventType, topicKey }) => {
    const credentials = getCredentials();

    if (!hasValidCredentials(credentials)) {
      return;
    }

    const clientMessageId = createUUID();

    pendingWireOps.set(clientMessageId, {
      service,
      eventType,
      topicKey,
      action: WIRE_ACTION_SUBSCRIBE,
    });

    sendOnSocket(
      buildSubscribeMessage({
        service,
        eventType,
        entityRef: credentials.entityRef,
        token: credentials.token,
        apiKey: credentials.apiKey,
        clientMessageId,
      })
    );
  };

  const sendWireUnsubscribe = ({ service, eventType, topicKey }) => {
    const credentials = getCredentials();

    if (!hasValidCredentials(credentials)) {
      return;
    }

    const clientMessageId = createUUID();

    pendingWireOps.set(clientMessageId, {
      service,
      eventType,
      topicKey,
      action: WIRE_ACTION_UNSUBSCRIBE,
    });

    sendOnSocket(
      buildUnsubscribeMessage({
        service,
        eventType,
        entityRef: credentials.entityRef,
        token: credentials.token,
        apiKey: credentials.apiKey,
        clientMessageId,
      })
    );
  };

  const resubscribeAllTopics = () => {
    topicRefCount.forEach(topic => {
      if (topic.count > 0) {
        sendWireSubscribe(topic);
      }
    });
  };

  const clearTopicSubscriptions = topicKey => {
    topicRefCount.delete(topicKey);
    serverSubscribed.delete(topicKey);

    subscriptions.forEach((subscription, componentSubscriptionKey) => {
      if (subscription.topicKey === topicKey) {
        subscriptions.delete(componentSubscriptionKey);
      }
    });
  };

  const handleSubscribeSucceeded = pendingOp => {
    notifyTopicSubscriptions(pendingOp?.topicKey, CALLBACK_ON_SUBSCRIBED, {
      service: pendingOp?.service,
      eventType: pendingOp?.eventType,
    });
  };

  const handleSubscribeFailed = (pendingOp, message) => {
    console.error('[uiPushClient] WebSocket subscribe failed', {
      service: pendingOp?.service,
      eventType: pendingOp?.eventType,
      message,
    });

    notifyTopicSubscriptions(pendingOp?.topicKey, CALLBACK_ON_ERROR, {
      type: ERROR_TYPE_SUBSCRIBE,
      service: pendingOp?.service,
      eventType: pendingOp?.eventType,
      message,
    });

    clearTopicSubscriptions(pendingOp?.topicKey);

    if (getTotalRefCount() === 0) {
      closeSocket();
    }
  };

  const handleUnsubscribeFailed = (pendingOp, message) => {
    console.error('[uiPushClient] WebSocket unsubscribe failed', {
      service: pendingOp?.service,
      eventType: pendingOp?.eventType,
      message,
    });

    notifyTopicSubscriptions(pendingOp?.topicKey, CALLBACK_ON_ERROR, {
      type: ERROR_TYPE_UNSUBSCRIBE,
      service: pendingOp?.service,
      eventType: pendingOp?.eventType,
      message,
    });
  };

  const handleInboundData = rawData => {
    const message = parseInboundMessage(rawData);

    if (!message) {
      notifyAllSubscriptions(CALLBACK_ON_ERROR, {
        type: ERROR_TYPE_MESSAGE,
        data: rawData,
      });

      return;
    }

    if (shouldIgnoreInboundMessage(message)) {
      handleProtocolMessage(message, {
        pendingWireOps,
        serverSubscribed,
        onSubscribeSucceeded: handleSubscribeSucceeded,
        onSubscribeFailed: handleSubscribeFailed,
        onUnsubscribeFailed: handleUnsubscribeFailed,
      });

      return;
    }

    const handlers = getHandlersForMessage(message, subscriptions);

    handlers.forEach(handler => {
      handler(message);
    });
  };

  const attachSocketHandlers = ws => {
    ws.onopen = () => {
      if (ws !== socket) {
        return;
      }

      reconnectAttempt = 0;
      flushOutboundQueue();
      startPinging();
      resubscribeAllTopics();
      notifyAllSubscriptions(CALLBACK_ON_CONNECT, { url: ws?.url });
    };

    ws.onmessage = event => {
      if (ws !== socket) {
        return;
      }

      handleInboundData(event?.data);
    };

    ws.onerror = event => {
      if (ws !== socket) {
        return;
      }

      console.error('[uiPushClient] WebSocket error', {
        url: ws?.url,
        readyState: ws?.readyState,
        event,
      });

      notifyAllSubscriptions(CALLBACK_ON_ERROR, {
        type: ERROR_TYPE_CONNECTION,
        url: ws?.url,
        readyState: ws?.readyState,
        event,
      });

      clearPingTimer();
    };

    ws.onclose = () => {
      if (ws !== socket) {
        return;
      }

      clearPingTimer();
      clearWireState();
      socket = null;

      notifyAllSubscriptions(CALLBACK_ON_DISCONNECT, { url: ws?.url });

      if (getTotalRefCount() > 0) {
        scheduleReconnect();
      }
    };
  };

  const connect = () => {
    const credentials = getCredentials();

    if (!hasValidCredentials(credentials)) {
      return;
    }

    if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
      return;
    }

    const baseWsUrl = config.getWsUrl(window.location.hostname);
    const wsUrl = buildAuthenticatedWsUrl(baseWsUrl, credentials);
    const ws = new WebSocket(wsUrl);

    socket = ws;
    attachSocketHandlers(ws);
  };

  const scheduleReconnect = () => {
    clearReconnectTimer();

    const delay = Math.min(config.reconnectBaseDelayMs * 2 ** reconnectAttempt, MAX_RECONNECT_DELAY_MS);

    reconnectAttempt += 1;

    reconnectTimer = setTimeout(() => {
      connect();
    }, delay);
  };

  const closeSocket = () => {
    clearPingTimer();
    clearReconnectTimer();
    clearWireState();

    if (socket) {
      const closingSocket = socket;

      socket = null;
      closingSocket.close();
    }
  };

  const ensureConnection = () => {
    if (getTotalRefCount() === 0) {
      return;
    }

    connect();
  };

  const incrementTopicRef = (service, eventType) => {
    const topicKey = getTopicKey(service, eventType);
    const existing = topicRefCount.get(topicKey);

    if (existing) {
      existing.count += 1;

      return;
    }

    topicRefCount.set(topicKey, { service, eventType, topicKey, count: 1 });
    ensureConnection();

    if (socket?.readyState === WebSocket.OPEN) {
      sendWireSubscribe({ service, eventType, topicKey });
    }
  };

  const decrementTopicRef = topicKey => {
    const existing = topicRefCount.get(topicKey);

    if (!existing || existing.count <= 0) {
      return;
    }

    existing.count -= 1;

    if (existing.count === 0) {
      topicRefCount.delete(topicKey);
      sendWireUnsubscribe(existing);
    }

    if (getTotalRefCount() === 0) {
      closeSocket();
    }
  };

  const handleCredentialsChange = () => {
    const credentials = getCredentials();
    const credentialsKey = getCredentialsKey(credentials);

    if (!hasValidCredentials(credentials)) {
      lastCredentialsKey = null;
      closeSocket();

      return;
    }

    if (lastCredentialsKey === credentialsKey) {
      return;
    }

    const hadPreviousCredentials = Boolean(lastCredentialsKey);

    lastCredentialsKey = credentialsKey;

    if (!hadPreviousCredentials) {
      ensureConnection();

      return;
    }

    closeSocket();
    ensureConnection();
  };

  const registerCredentialsListener = () => {
    if (credentialsListenerRegistered) {
      return;
    }

    credentialsListenerRegistered = true;

    addListener(() => {
      handleCredentialsChange();
    });
  };

  const subscribe = (service, eventType, handler, callbacks = {}) => {
    if (!service || !eventType || typeof handler !== 'function') {
      throw new Error('subscribe requires service, eventType and handler');
    }

    registerCredentialsListener();

    const componentSubscriptionKey = createUUID();
    const topicKey = getTopicKey(service, eventType);
    const { onError, onSubscribed, onUnsubscribed, onConnect, onDisconnect } = callbacks ?? {};

    subscriptions.set(componentSubscriptionKey, {
      service,
      eventType,
      topicKey,
      [CALLBACK_ON_MESSAGE]: handler,
      [CALLBACK_ON_ERROR]: onError,
      [CALLBACK_ON_SUBSCRIBED]: onSubscribed,
      [CALLBACK_ON_UNSUBSCRIBED]: onUnsubscribed,
      [CALLBACK_ON_CONNECT]: onConnect,
      [CALLBACK_ON_DISCONNECT]: onDisconnect,
    });
    incrementTopicRef(service, eventType);
    handleCredentialsChange();

    if (socket?.readyState === WebSocket.OPEN) {
      // already connected, so invoke onConnect immediately
      onConnect?.({ url: socket?.url });
    }

    if (serverSubscribed.has(topicKey)) {
      // server already subscribed to the topic, so invoke onSubscribed immediately
      onSubscribed?.({ service, eventType });
    }

    return componentSubscriptionKey;
  };

  const unsubscribe = componentSubscriptionKey => {
    const subscription = subscriptions.get(componentSubscriptionKey);

    if (!subscription) {
      return;
    }

    subscriptions.delete(componentSubscriptionKey);

    subscription?.[CALLBACK_ON_UNSUBSCRIBED]?.({
      service: subscription?.service,
      eventType: subscription?.eventType,
    });

    decrementTopicRef(subscription.topicKey);
  };

  const configure = nextConfig => {
    if (!nextConfig) {
      return;
    }

    const hadSubscriptions = getTotalRefCount() > 0;

    config = {
      ...config,
      ...nextConfig,
      getCredentials: nextConfig.getCredentials ?? config.getCredentials,
    };

    if (nextConfig.wsPath !== undefined) {
      config.wsPath = nextConfig.wsPath;
      config.getWsUrl = hostname => getWsUrl(hostname, config.wsPath);
    } else if (nextConfig.getWsUrl) {
      config.getWsUrl = nextConfig.getWsUrl;
    }

    if (hadSubscriptions) {
      closeSocket();
      lastCredentialsKey = null;
      handleCredentialsChange();
      ensureConnection();
    }
  };

  return {
    subscribe,
    unsubscribe,
    configure,
  };
};
